import { useCallback, useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'
import { useEditorRef } from 'platejs/react'
import type { PlateElementProps } from 'platejs/react'

import { getPublishUrl } from '@buffer-mono/shared-utils/urls'
import { Button, CoachMark, SimpleSpinner } from '@buffer-mono/popcorn'
import {
  InlineCombobox,
  useInlineComboboxSelect,
} from '@buffer-mono/popcorn/rich-text'

import { useSaveCallbacks } from '~publish/components/Composer/save'
import {
  useComposerMode,
  useIsDraftMode,
} from '~publish/components/Composer/store/appSlice/hooks'
import { useHasOrganizationAction } from '~publish/contexts/AccountContext'
import {
  connectChannel,
  isChannelConnectionAvailable,
} from '~publish/helpers/channelConnections'
import AppStore from '~publish/legacy/composer/composer/stores/AppStore'
import { selectedChannelsForTracking } from '~publish/legacy/composer/composer/editor/ComposerEditor/hooks/useSelectedChannelsForTracking'
import { useAutocompleteSuggestions } from '~publish/legacy/editor/plugins/autocomplete'
import { selectAndInsertSuggestion } from '~publish/legacy/editor/plugins/autocomplete/transforms/selectAndInsertSuggestion'
import { AutocompleteSuggestion } from '~publish/legacy/editor/plugins/autocomplete/ui/AutocompleteSuggestion'
import type { BufferEditor } from '~publish/legacy/editor/BufferEditor/types.plate'
import {
  EditorTooltip,
  TooltipButton,
  TooltipText,
} from '~publish/legacy/editor/components/EditorTooltip'
import {
  channelSchedule,
  channelSchedulePostEdit,
} from '~publish/legacy/routes'

import type { LinkedInMentionSearchError } from '../api/getNewSuggestionsOnMentionUpdate'
import { resolveLinkedInEntityUrl } from '../api/resolveLinkedInEntityUrl'
import { LINKEDIN_PROFILE_URL_REGEX } from '../LinkedInAnnotations'
import { LinkedInAnnotationInputElement } from '../nodes/LinkedInAnnotationInputElement'
import { getRecentItems, removeRecentItem } from '../queries/recentSuggestions'

import styles from './LinkedInMentionUI.module.css'

type LinkedInMentionUIProps = PlateElementProps<LinkedInAnnotationInputElement>

const isSearchError = (error: unknown): error is LinkedInMentionSearchError =>
  typeof error === 'object' && error !== null && 'type' in error

type LinkedInUrlPasteResolverProps = {
  search: string
  editor: BufferEditor
  onResolvingChange: (isResolving: boolean) => void
  onNotFound: (notFound: boolean) => void
}

/**
 * `useInlineComboboxSelect` requires an `InlineCombobox.Root` ancestor, so
 * this URL-paste resolution logic lives in its own component rendered
 * inside `Root` rather than in `LinkedInMentionUI` itself (which renders
 * `Root`, but isn't a descendant of it).
 */
const LinkedInUrlPasteResolver = ({
  search,
  editor,
  onResolvingChange,
  onNotFound,
}: LinkedInUrlPasteResolverProps): null => {
  // useInlineComboboxSelect() returns a new function identity on every
  // InlineCombobox.Root render — including the one onResolvingChange itself
  // triggers — so keeping it in the effect's own dependency list below would
  // tear the effect down and restart it on every loading-state change,
  // cancelling the in-flight lookup before it can resolve. A ref sidesteps
  // that without needing it in the dependency array.
  const selectInlineCombobox = useInlineComboboxSelect()
  const selectInlineComboboxRef = useRef(selectInlineCombobox)
  selectInlineComboboxRef.current = selectInlineCombobox

  useEffect(() => {
    onNotFound(false)
    if (!search.match(/^https?:\/\//)) return
    // Profile URLs (linkedin.com/in/...) don't resolve directly — they seed
    // a real search instead, which LinkedInMentionUI's own search already
    // covers via getNewSuggestionsOnMentionUpdate.
    if (LINKEDIN_PROFILE_URL_REGEX.test(search)) return

    const channelId =
      editor.getEditorComposerData?.(editor)?.selectedProfilesForService[0]?.id
    if (!channelId) return

    const trackingDetails = selectedChannelsForTracking({
      serviceName: 'linkedin',
    })

    let cancelled = false
    onResolvingChange(true)
    void (async (): Promise<void> => {
      try {
        const entityDetails = await resolveLinkedInEntityUrl(search, channelId)
        if (cancelled) return

        // `null` means the pasted URL isn't a recognized LinkedIn entity
        // URL at all; `undefined` means it matched that shape but no entity
        // was found. Neither has a mention to insert, so both are reported
        // as not-found, matching the pre-migration behavior.
        if (!entityDetails) {
          onNotFound(true)
          trackingDetails.sendMentionURLPastedFailed(search)
          return
        }

        trackingDetails.sendMentionURLPastedSucceeded(search)
        selectInlineComboboxRef.current(() => {
          editor.tf.withNewBatch(() => {
            editor.tf.insertNodes(
              LinkedInAnnotationInputElement.new(entityDetails),
            )
            editor.tf.move({ distance: 1, unit: 'offset' })
            editor.tf.insertText(' ')
          })
        })
      } catch {
        if (!cancelled) {
          onNotFound(true)
          trackingDetails.sendMentionURLPastedFailed(search)
        }
      } finally {
        if (!cancelled) onResolvingChange(false)
      }
    })()

    return (): void => {
      cancelled = true
      onResolvingChange(false)
    }
  }, [search, editor, onResolvingChange, onNotFound])

  return null
}

export const LinkedInMentionUI = ({
  attributes,
  children,
  element,
}: LinkedInMentionUIProps): JSX.Element | null => {
  const editor = useEditorRef() as BufferEditor
  const [search, setSearch] = useState(() => element.initialQuery ?? '')
  const [isSavingAsDraft, setIsSavingAsDraft] = useState(false)
  const [isCoachMarkOpen, setIsCoachMarkOpen] = useState(true)
  const [urlNotFound, setUrlNotFound] = useState(false)
  const [urlResolving, setUrlResolving] = useState(false)
  const rootRef = useRef<HTMLSpanElement>(null)

  const { saveAsDraft, updatePost } = useSaveCallbacks()
  const canManageChannels = useHasOrganizationAction('manageChannels')

  const composerMode = useComposerMode()
  const draftMode = useIsDraftMode()
  const isNewPostMode = !draftMode && composerMode !== 'edit'

  const onOpenClick = useCallback(() => {
    window.open(element.data?.link, '_blank')
  }, [element.data?.link])

  const {
    items,
    setItems,
    error,
    isLoading: searchLoading,
  } = useAutocompleteSuggestions(
    editor,
    LinkedInAnnotationInputElement,
    search,
    // Once a mention resolves (element.data set), stop fetching entirely —
    // an empty search is itself meaningful (fetches recent items), so that
    // can no longer double as the "stop" signal.
    !element.data,
  )
  const permissionErrorChannelId =
    isSearchError(error) && error.type === 'permission'
      ? error.channelId
      : undefined

  const handleSelect = useCallback(
    (mention: (typeof items)[number]) => {
      selectAndInsertSuggestion(LinkedInAnnotationInputElement)(editor, mention)
    },
    [editor],
  )

  const handleRemoveRecentItem = useCallback(
    (key: string) => {
      if (!removeRecentItem(key)) return
      setItems(getRecentItems() ?? [])
    },
    [setItems],
  )

  const mentionItems = items.map((mention) => (
    <InlineCombobox.Item
      key={mention.key}
      value={mention.key}
      textValue={mention.text}
      onSelect={() => handleSelect(mention)}
      className={styles.item}
    >
      <AutocompleteSuggestion
        mention={mention}
        onRemoveRecentItem={
          // Only for the recents list: a real search result can share a
          // key with a past recent pick, and isRecentItem() checks the
          // key alone, not which list rendered it.
          search.length === 0 ? handleRemoveRecentItem : undefined
        }
      />
    </InlineCombobox.Item>
  ))

  if (element.data) {
    const { link, localizedName } = element.data
    // this has to be able to display both autocomplete and pasted mentions as urls
    return (
      <span {...attributes} className={styles.mention} contentEditable={false}>
        <EditorTooltip trigger={<span>{localizedName}</span>}>
          <TooltipText title={link}>{link}</TooltipText>
          <TooltipButton onClick={onOpenClick}>Open</TooltipButton>
        </EditorTooltip>
        {children}
      </span>
    )
  }

  if (permissionErrorChannelId && isCoachMarkOpen) {
    return (
      <span {...attributes}>
        <CoachMark
          onDismiss={(): void => setIsCoachMarkOpen(false)}
          dismissTimeout={null}
          open={isCoachMarkOpen}
        >
          <CoachMark.Overlay style={{ display: 'inline' }}>
            <span className={styles.wrapper} contentEditable={false}>
              {children}
            </span>
          </CoachMark.Overlay>

          <CoachMark.Content>
            <CoachMark.Title>
              Let&apos;s setup LinkedIn mentions!
            </CoachMark.Title>
            <CoachMark.Description>
              To get everything ready for mentioning, a quick channel refresh is
              needed.
            </CoachMark.Description>
            <CoachMark.Footer>
              <CoachMark.Dismiss>
                <Button variant="tertiary">Not Now</Button>
              </CoachMark.Dismiss>

              <Button
                variant="primary"
                disabled={isSavingAsDraft}
                onClick={(): void => {
                  const channelId = permissionErrorChannelId
                  if (!channelId) return

                  if (isChannelConnectionAvailable()) {
                    const { isSavingPossible, isSavingDraftPossible } =
                      AppStore.getAppState()

                    if (!isSavingPossible && !isSavingDraftPossible) {
                      const destinationUrl = getPublishUrl(
                        `channels/${channelId}`,
                      )
                      setIsSavingAsDraft(true)

                      connectChannel({
                        service: 'linkedin',
                        isRefreshingConnection: true,
                        skipCelebrationModal: true,
                        cta: 'publish-composer-linkedinMentions-refresh-1',
                        selectedRefreshChannelId: channelId,
                        canManageChannels,
                        destinationUrl,
                      })
                      return
                    }

                    setIsSavingAsDraft(true)

                    const savePromise = isNewPostMode
                      ? saveAsDraft()
                      : updatePost()

                    savePromise
                      // eslint-disable-next-line promise/always-return
                      .then(({ success }): void => {
                        if (!success) {
                          setIsSavingAsDraft(false)
                          return
                        }

                        const { updateId } = AppStore.getMetaData()
                        const pathname =
                          !isNewPostMode && updateId
                            ? channelSchedulePostEdit.getRoute({
                                channelId,
                                postId: updateId,
                              })
                            : channelSchedule.getRoute(channelId, {
                                tab: 'drafts',
                              })
                        const destinationUrl = getPublishUrl(pathname)

                        connectChannel({
                          service: 'linkedin',
                          isRefreshingConnection: true,
                          skipCelebrationModal: true,
                          cta: 'publish-composer-linkedinMentions-refresh-1',
                          selectedRefreshChannelId: channelId,
                          canManageChannels,
                          destinationUrl,
                        })
                      })
                      .catch(() => {
                        setIsSavingAsDraft(false)
                      })
                  } else {
                    setIsSavingAsDraft(true)

                    const savePromise = isNewPostMode
                      ? saveAsDraft()
                      : updatePost()

                    savePromise
                      // eslint-disable-next-line promise/always-return
                      .then(({ success }): void => {
                        if (!success) {
                          setIsSavingAsDraft(false)
                          return
                        }
                        window.location.href =
                          getPublishUrl('settings/channels')
                      })
                      .catch(() => {
                        setIsSavingAsDraft(false)
                      })
                  }
                }}
              >
                {isSavingAsDraft ? (
                  <>
                    <SimpleSpinner /> Saving as Draft
                  </>
                ) : (
                  'Refresh'
                )}
              </Button>
            </CoachMark.Footer>
          </CoachMark.Content>
        </CoachMark>
      </span>
    )
  }

  const isLoading = urlResolving || searchLoading
  const notFound = urlNotFound

  return (
    <span {...attributes}>
      <InlineCombobox.Root
        ref={rootRef}
        trigger={element.trigger}
        value={search}
        onValueChange={setSearch}
      >
        <LinkedInUrlPasteResolver
          search={search}
          editor={editor}
          onResolvingChange={setUrlResolving}
          onNotFound={setUrlNotFound}
        />
        <InlineCombobox.Input aria-label="Search LinkedIn mentions" />
        <InlineCombobox.Content
          anchorRef={rootRef}
          className={styles.suggestionsPopover}
        >
          {search.length === 0 && (
            <InlineCombobox.Status className={styles.helpText}>
              Mention a <strong>connection</strong> or paste a{' '}
              <strong>LinkedIn Page URL</strong>
            </InlineCombobox.Status>
          )}
          {search.length >= 1 && search.length <= 2 && (
            <InlineCombobox.Status className={styles.info}>
              <span className={styles.infoText}>
                Please type at least 3 characters
              </span>
            </InlineCombobox.Status>
          )}
          {search.length >= 3 && notFound && (
            <InlineCombobox.Status className={styles.info}>
              <span className={styles.infoText}>
                We couldn&apos;t find a LinkedIn Page at this URL
              </span>
            </InlineCombobox.Status>
          )}
          {search.length >= 3 &&
            !notFound &&
            !isLoading &&
            items.length === 0 && (
              <InlineCombobox.Status className={styles.info}>
                <span className={styles.infoText}>
                  No LinkedIn connections matched
                </span>
              </InlineCombobox.Status>
            )}
          {items.length > 0 && (
            <InlineCombobox.List
              aria-label="LinkedIn mention results"
              className={styles.list}
            >
              {search.length === 0 ? (
                <InlineCombobox.Group>
                  <InlineCombobox.GroupLabel>Recents</InlineCombobox.GroupLabel>
                  {mentionItems}
                </InlineCombobox.Group>
              ) : (
                mentionItems
              )}
            </InlineCombobox.List>
          )}
        </InlineCombobox.Content>
      </InlineCombobox.Root>
      {isLoading && <SimpleSpinner className={styles.spinner} />}
      {children}
    </span>
  )
}
